'use client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import useFormattedDate from '@/hooks/useFormattedDate';
import { useRootStore } from '@/providers/root-store-provider';
import { TOrganizationFAQ } from '@/services/api/organization-api';
import { myServicesData } from '@/services/api/services-api';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInYears } from 'date-fns';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import React from 'react';
import { useTranslation } from 'react-i18next';

const RegistrationSummary: React.FC<{ organization: TOrganizationFAQ }> = ({
  organization
}) => {
  const { t } = useTranslation(['newBooking', 'common']);
  const router = useRouter();
  const setStep = useRootStore((store) => store.setStep);
  const removeStep = useRootStore((store) => store.removeStep);
  const { formattedDate } = useFormattedDate();

  const queryClient = useQueryClient();
  const saleData: myServicesData | undefined = queryClient.getQueryData([
    'createSale'
  ]);

  if (!saleData) {
    removeStep(1);
    return;
  }

  const startTime = formattedDate({
    date: saleData.reservations[0].startTime,
    dateFormat: 'date'
  });

  const studentData = saleData.participants[0];
  const additionalData = studentData.additionalData;
  const parents = additionalData.parents;
  const authorizedToPickUp = additionalData.authorizedToPickUp;

  const content: {
    label: string;
    value: {
      label: string;
      value: string;
    }[];
  }[] = [
    {
      label: t('inscriptionData'),
      value: [
        {
          label: t('registrationSummary.information.curse'),
          value: saleData.service?.name
        },
        {
          label: t('registrationSummary.information.startDate'),
          value: startTime
        }
      ]
    },
    {
      label: t('studentData'),
      value: [
        {
          label: t('stepParticipants.form.firstName'),
          value: `${studentData?.firstName} ${studentData?.lastName}`
        },
        {
          label: t('age'),
          value: studentData?.birthdate
            ? differenceInYears(new Date(), studentData?.birthdate)
            : ''
        },
        {
          label: t('insurance'),
          value: additionalData?.insurance
        }
      ]
    },
    {
      label: t('responsible'),
      value: parents.map((parent: any, index: number) => ({
        label: `${parent?.connection}`,
        value: `${parent?.firstName} ${parent?.lastName}`
      }))
    },
    {
      label: t('authorizedToPickUp'),
      value: [
        {
          label: t('stepParticipants.form.firstName'),
          value: `${authorizedToPickUp?.lastName}, ${authorizedToPickUp?.firstName}`
        },
        {
          label: t('phone'),
          value: authorizedToPickUp?.phone
        }
      ]
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex justify-between mb-6 items-center gap-4">
          <h3 className="font-bold">{t('registrationSummary.title')}</h3>
          <Image
            src="/images/viento-en-popa-logo.jpeg"
            width={75}
            height={75}
            style={{ objectFit: 'contain' }}
            alt="Viento en Popa"
          />
        </div>
        <Card className="w-full">
          <CardContent className="p-5 sm:p-10 space-y-6 ">
            {content.map((value, index) => (
              <div key={`${value.label}-${index}`}>
                <h2 className="font-bold">{value.label}</h2>
                {value.value.map((obj, index) => (
                  <div
                    key={`${value.value}-${index}`}
                    className="px-5 my-5 flex gap-2"
                  >
                    <p>{obj.label}:</p>
                    <p>{obj.value}</p>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-col gap-4">
        <Button
          onClick={() => router.push(`/bookings/${saleData.id}`)}
          type="button"
          className="w-full"
        >
          {t('registrationSummary.button.seeRegistration')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setStep(1)}
        >
          {t('registrationSummary.button.anotherRegistration')}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationSummary;
